import { useEffect, useRef } from "react";
import { useTonConnectUI, CHAIN } from "@tonconnect/ui-react";
import type { Wallet as TonConnectWallet } from "@tonconnect/ui-react";
import type { UseMutationResult } from "@tanstack/react-query";
import { deriveWalletRegistrationPayload, walletMatches } from "./address-normalizers";
import { useProofChallenge } from "./useProofChallenge";
import { useWalletProofVerification } from "./useWalletProofVerification";
import type { Wallet } from "../../../entities/wallet";
import type { CreateWalletRequest } from "../../../shared/api/wallet";
import { isHttpError } from "../../../shared/api/http";
import { TelegramWebAppUtils } from "../../../shared/api/telegram";
import { useTranslation } from "../../../shared/config/i18n";
import { logger } from "../../../shared/utils/logger";

interface UseTonProofOnConnectParams {
  tonWallet: TonConnectWallet | null;
  wallets: Wallet[] | undefined;
  isLoading: boolean;
  isError: boolean;
  createWalletMutation: UseMutationResult<Wallet, unknown, CreateWalletRequest>;
}

/** Process the current connection once wallet data is available, including restores. */
export const useTonProofOnConnect = ({
  tonWallet,
  wallets,
  isLoading,
  isError,
  createWalletMutation,
}: UseTonProofOnConnectParams) => {
  const [tonConnectUI] = useTonConnectUI();
  const { t } = useTranslation();
  const { challengeRef, clearChallenge } = useProofChallenge({ tonWallet });
  const { verifyProof, verifyingRef } = useWalletProofVerification({ challengeRef });
  const currentWalletRef = useRef(tonWallet);
  currentWalletRef.current = tonWallet;
  const mountedRef = useRef(true);
  const attemptedRef = useRef<TonConnectWallet | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!tonWallet) {
      if (attemptedRef.current) clearChallenge();
      attemptedRef.current = null;
      return;
    }
    if (tonWallet.account.chain === CHAIN.TESTNET || isLoading || isError || !wallets) return;

    const existingWallet = wallets.find(w => walletMatches(w, tonWallet.account.address));
    if (existingWallet?.isVerified || attemptedRef.current === tonWallet) return;

    // Lock the entire registration + verification operation before its first await.
    // Query invalidation and mutation updates must not submit the same proof twice.
    attemptedRef.current = tonWallet;
    const isCurrent = () => mountedRef.current && currentWalletRef.current === tonWallet;

    const reconnect = async () => {
      if (!isCurrent()) return;
      clearChallenge();
      // Do not allow a new connection without a fresh proof while it is loading.
      tonConnectUI.setConnectRequestParameters({ state: 'loading' });
      await tonConnectUI.disconnect();
      if (!mountedRef.current || (currentWalletRef.current && currentWalletRef.current !== tonWallet)) return;
      TelegramWebAppUtils.alerts.showAlert(t('wallet.verificationReconnect'));
      await tonConnectUI.openModal();
    };

    const processConnection = async () => {
      try {
        const item = tonWallet.connectItems?.tonProof;
        if (!item || !('proof' in item)) {
          throw new Error('Wallet connection has no proof');
        }
        let dbWallet = existingWallet;
        if (!dbWallet) {
          const registration = deriveWalletRegistrationPayload(tonWallet);
          if (!registration) {
            throw new Error('Wallet registration data is incomplete');
          }
          dbWallet = await createWalletMutation.mutateAsync(registration.request);
        }
        if (!isCurrent()) return;
        if (!dbWallet.isVerified) await verifyProof(tonWallet, dbWallet, item.proof);
      } catch (error) {
        if (!isCurrent()) return;
        logger.warn('[useTonProofOnConnect] Wallet verification failed', error);
        if (isHttpError(error) && error.status === 401) {
          const { authService, telecomTokenService } = await import('../../../entities/user');
          await authService.clearTokens();
          await telecomTokenService.clearToken();
          const { telecomTokenByPhoneService } = await import('../../../entities/user/telecom-token-by-phone-service');
          telecomTokenByPhoneService.clearAllTokens();
          if (isCurrent()) window.location.reload();
          return;
        }
        // A consumed/expired/missing proof cannot be fixed by waiting or signing
        // a different challenge on the server. Ask the wallet for a fresh proof.
        await reconnect();
      }
    };

    void processConnection().catch(error => {
      logger.warn('[useTonProofOnConnect] Could not reconnect wallet', error);
      if (isCurrent()) TelegramWebAppUtils.alerts.showAlert(t('wallet.verificationReconnect'));
    });
  }, [tonWallet, wallets, isLoading, isError, createWalletMutation, verifyProof,
    clearChallenge, tonConnectUI, t]);

  return { isVerifying: verifyingRef.current.size > 0 };
};
